/*
 * Dispatch countdown
 * ==================
 *
 * The store's only urgency device, and the reason it is allowed on the page is
 * that it is true: it counts down to a cutoff the warehouse actually ships to,
 * in the warehouse's own timezone, and it stops claiming anything the moment
 * that cutoff passes. After the cutoff — and at the weekend — it names the next
 * working day instead of resetting to a fresh, meaningless 24 hours.
 *
 * Every string is passed in from Liquid so the copy stays editable and
 * translatable; this file owns the arithmetic and nothing else.
 */
(() => {
  if (customElements.get('airflux-dispatch')) return;

  const DAY_MS = 86400000;

  /**
   * "Now", as the warehouse sees it.
   *
   * The shopper's own clock is the wrong clock: someone ordering at 16:30 in
   * Berlin is ordering at 15:30 in Leicester, and a countdown run off their
   * device would promise them a dispatch that has already closed. Everything
   * below therefore works in the merchant's timezone, read back out of Intl as
   * plain numbers.
   */
  function localNow(timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return {
      day: days.indexOf(get('weekday')),
      // Midnight reads as "24" in some ICU builds, which would put the day's
      // elapsed seconds a full day ahead of where they are.
      seconds: (Number(get('hour')) % 24) * 3600 + Number(get('minute')) * 60 + Number(get('second')),
    };
  }

  class AirfluxDispatch extends HTMLElement {
    connectedCallback() {
      const [h, m] = (this.getAttribute('cutoff') || '15:00').split(':');
      this.cutoff = (Number(h) || 0) * 3600 + (Number(m) || 0) * 60;
      this.zone = this.getAttribute('timezone') || 'Europe/London';
      // "1,2,3,4,5" — the days the warehouse dispatches on, Sunday being 0.
      this.working = new Set(
        (this.getAttribute('working-days') || '1,2,3,4,5')
          .split(',')
          .map((d) => Number(d.trim()))
          .filter((d) => d >= 0 && d <= 6)
      );

      this.todayText = this.getAttribute('today-text') || 'for dispatch today';
      this.nextText = this.getAttribute('next-text') || 'Next dispatch';
      // Not `this.slot`: HTMLElement already owns that name as a string
      // attribute reflection, so assigning an element to it silently stores
      // "[object HTMLSpanElement]" and every write below fails.
      this.out = this.querySelector('[data-dispatch-out]');

      if (!this.out || !this.working.size) return;

      this.tick();
      // Once a minute is enough: the readout is in hours and minutes, so a
      // per-second timer would repaint sixty times for one visible change.
      this.timer = setInterval(() => this.tick(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const now = localNow(this.zone);
      const open = this.working.has(now.day) && now.seconds < this.cutoff;

      if (open) {
        const left = this.cutoff - now.seconds;
        const hours = Math.floor(left / 3600);
        const mins = Math.ceil((left % 3600) / 60);
        const clock = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
        this.out.innerHTML = `Order within <span class="af-dispatch__time">${clock}</span> ${escapeHtml(
          this.todayText
        )}`;
        return;
      }

      // Past the cutoff, or a day we do not ship on. Say which day it does go,
      // rather than counting down to a dispatch that is no longer available.
      this.out.innerHTML = `${escapeHtml(this.nextText)} <span class="af-dispatch__time">${escapeHtml(
        this.nextWorkingDay(now.day)
      )}</span>`;
    }

    /** The next day the warehouse ships, named the way a person would say it. */
    nextWorkingDay(today) {
      for (let ahead = 1; ahead <= 7; ahead += 1) {
        const day = (today + ahead) % 7;
        if (!this.working.has(day)) continue;
        if (ahead === 1) return 'tomorrow';
        const when = new Date(Date.now() + ahead * DAY_MS);
        return new Intl.DateTimeFormat('en-GB', { timeZone: this.zone, weekday: 'long' }).format(when);
      }
      return 'the next working day';
    }
  }

  function escapeHtml(value) {
    return String(value).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );
  }

  customElements.define('airflux-dispatch', AirfluxDispatch);
})();
